import qrcode from 'qrcode-generator';
import type {
  AnnouncementsProps, ClockProps, CountdownProps, DonationQrProps, DonationsProps, EventsProps, HeadingProps, JumuahProps, LogoProps, Masjid, ParagraphProps, PostersProps,
  PrayerState, PrayerTimesProps, ScriptureProps, TickerProps, TimetableIndex, Widget,
} from '@noorview/core';
import {
  scriptureFor, autoInk, hexToRgb, rgba, rgbToHex, PRAYER_LABEL, addDays, clockDuration, compactDuration, dailyPick, dayTimes, formatDate, formatHijri, hhmm, hijriFor,
  designOf, nextJumuah, parseYmd, zonedToInstant, HEADING_MAX, PARAGRAPH_MAX, PARAGRAPH_TITLE_MAX, makruhAt, nextMakruh, type MakruhPeriod, type MakruhProps,
} from '@noorview/core';

/* ---------- shared ---------- */

export interface Env {
  masjid: Masjid;
  timetable: TimetableIndex;
  /** 'editor' shows helpful empty states; 'screen' shows nothing where there's no content. */
  mode: 'screen' | 'editor' | 'thumbnail';
  /** Box size in display pixels, for choosing layouts. */
  box: { width: number; height: number };
}

export interface Tick { now: Date; day: string; state: PrayerState }

export interface WidgetView {
  /** Re-renders from the widget's settings. */
  update(w: Widget, env: Env): void;
  /** Called once a second. Returns true when text lengths may have changed and the widget should be re-fitted. */
  tick(t: Tick, env: Env): boolean;
  destroy(): void;
}

export { el, setText, t12, wall12 } from './dom';
import { el, setText, t12, wall12 } from './dom';
import * as D from './designs';
import * as D2 from './designs2';
import * as DN from './donations';
import { countdownSource, jumuahSource, ramadanSource, timerFace } from './faces';

/* ---------- Prayer Times ---------- */

function prayerTimes(c: HTMLElement): WidgetView {
  let p: PrayerTimesProps;
  let tiles: Array<{ key: string; root: HTMLElement; n: HTMLElement; s: HTMLElement; j: HTMLElement }> = [];
  const head = el('div', 'pt-head'), title = el('div', 'nvd-label'), next = el('div', 'nvd-label nvd-acc');
  head.append(title, next);
  const grid = el('div', 'pt-grid'), eid = el('div', 'pt-eid');
  c.append(head, grid, eid);
  let dayKey = '', layoutKey = '', tarawih = false, lastLayout: 'row' | 'column' = 'row';
  const rebuild = () => {
    const layout = lastLayout;
    const keys = ['fajr', ...(p.showSunrise ? ['sunrise'] : []), 'dhuhr', 'asr', 'maghrib', 'isha', ...(tarawih ? ['tarawih'] : [])];
    const lk = keys.join() + layout;
    if (lk !== layoutKey) {
      layoutKey = lk; dayKey = '';
      grid.replaceChildren();
      tiles = keys.map((key) => {
        const root = el('div', 'pt-t'), n = el('div', 'pt-n'), s = el('div', 'pt-s'), j = el('div', 'pt-j');
        if (layout === 'column') { const times = el('div', 'pt-times'); times.append(s, j); root.append(n, times); } else root.append(n, s, j);
        grid.append(root);
        return { key, root, n, s, j };
      });
    }
    for (const t of tiles) paintTile(t.root, t.key === 'tarawih' ? undefined : p.prayerColors?.[t.key as keyof NonNullable<PrayerTimesProps['prayerColors']>], p.tileColor, p.tileText);
  };
  return {
    update(w, env) {
      p = w.props as PrayerTimesProps;
      const layout = p.layout === 'auto' ? (env.box.width / env.box.height > 1.25 ? 'row' : 'column') : p.layout;
      c.classList.toggle('pt-row', layout === 'row');
      c.classList.toggle('pt-col', layout === 'column');
      title.textContent = p.title || 'Prayer Times';
      head.style.display = p.title || p.showNext ? '' : 'none';
      next.style.display = p.showNext ? '' : 'none';
      lastLayout = layout === 'column' ? 'column' : 'row';
      rebuild();
      dayKey = '';
    },
    tick({ now, day, state }, env) {
      const tz = env.masjid.timezone;
      let changed = false;
      if (day !== dayKey) {
        const t = state.today;
        const tw = p.showTarawih !== false && !!t.tarawih;
        if (tw !== tarawih) { tarawih = tw; rebuild(); changed = true; }
        dayKey = day;
        for (const tile of tiles) {
          if (tile.key === 'tarawih') {
            changed = setText(tile.n, 'Tarawih') || changed;
            changed = setText(tile.s, wall12(t.tarawih, p.h12)) || changed;
            tile.j.style.display = p.showJamaah ? '' : 'none';
            changed = setText(tile.j, '—') || changed;
            tile.root.classList.add('no-jam');
            continue;
          }
          const k = tile.key as keyof typeof t.start;
          const isJumuah = k === 'dhuhr' && t.isFriday && t.jumuah.length > 0;
          changed = setText(tile.n, isJumuah ? 'Jumu‘ah' : PRAYER_LABEL[k]) || changed;
          changed = setText(tile.s, wall12(t.start[k], p.h12)) || changed;
          tile.j.style.display = p.showJamaah ? '' : 'none';
          // On Fridays, both Jumu'ah jama'ahs.
          const jam = k === 'sunrise' ? '—' : isJumuah ? t.jumuah.map((j) => wall12(j.jamaah, p.h12)).join(' · ') : wall12(t.jamaah[k as keyof typeof t.jamaah], p.h12);
          changed = setText(tile.j, jam) || changed;
          // The jama'ah is the big time; without one, the start time is.
          tile.root.classList.toggle('no-jam', !p.showJamaah || k === 'sunrise' || (!isJumuah && !t.jamaah[k as keyof typeof t.jamaah]));
        }
        const e = t.eid;
        eid.style.display = e ? '' : 'none';
        if (e) setText(eid, `${e.name}: ${e.takbeer ? `Takbeer ${wall12(e.takbeer, p.h12)} · ` : ''}Salah ${e.jamaats.map((j) => wall12(j, p.h12)).join(', ')}`);
      }
      for (const tile of tiles) tile.root.classList.toggle('on', !state.empty && tile.key === state.highlight);
      if (p.showNext && state.empty) changed = setText(next, 'Upload a timetable') || changed;
      else if (p.showNext) {
        const j = state.nextJamaah, s = state.nextStart;
        const useJ = j && state.current && j.prayer === state.current && j.at.getTime() < s.at.getTime();
        const m = useJ ? j! : s;
        const label = m.prayer === 'dhuhr' && state.today.isFriday && m.day === day && state.today.jumuah.length ? 'Jumu‘ah' : PRAYER_LABEL[m.prayer];
        changed = setText(next, `${label}${useJ ? ' jama‘ah' : ''} in ${compactDuration(m.at.getTime() - now.getTime())}`) || changed;
      }
      void tz;
      return changed;
    },
    destroy() {},
  };
}

/** Gives a prayer tile its own colour and readable text (the next prayer's highlight still wins). */
export function paintTile(root: HTMLElement, own: { tile?: string | null; text?: string | null } | undefined, tile?: string | null, text?: string | null) {
  const bg = own?.tile || tile || '';
  const ink = own?.text || text || (bg ? rgbToHex(autoInk(hexToRgb(bg))) : '');
  root.style.setProperty('--tile-bg', bg || '');
  if (!bg) root.style.removeProperty('--tile-bg');
  // A prayer given its own colour keeps it even when it's next; the highlight then shows as a ring.
  root.classList.toggle('own', !!own?.tile);
  if (ink) {
    const c = hexToRgb(ink);
    root.style.setProperty('--tile-ink', ink); root.style.setProperty('--ink2', rgba(c, 0.78)); root.style.setProperty('--ink3', rgba(c, 0.56));
  } else for (const k of ['--tile-ink', '--ink2', '--ink3']) root.style.removeProperty(k);
}

/* ---------- Clock ---------- */

function clock(c: HTMLElement): WidgetView {
  let p: ClockProps;
  const wrap = el('div', 'nvd-center');
  const time = el('div', 'ck-time'), hm = el('span'), sec = el('span', 'ck-sec');
  time.append(hm, sec);
  const date = el('div', 'ck-date'), hijri = el('div', 'ck-hijri');
  wrap.append(time, date, hijri);
  c.append(wrap);
  return {
    update(w) {
      p = w.props as ClockProps;
      sec.style.display = p.showSeconds ? '' : 'none';
      date.style.display = p.showDate ? '' : 'none';
      hijri.style.display = p.showHijri ? '' : 'none';
    },
    tick({ now, day }, env) {
      const tz = env.masjid.timezone;
      let ch = setText(hm, t12(now, tz, p.h12));
      if (p.showSeconds) setText(sec, String(now.getUTCSeconds()).padStart(2, '0'));
      if (p.showDate) ch = setText(date, formatDate(now, tz, 'long')) || ch;
      if (p.showHijri) ch = setText(hijri, formatHijri(hijriFor(day, env.masjid.prayer.hijriAdjust))) || ch;
      return ch;
    },
    destroy() {},
  };
}

/* ---------- Jumu'ah ---------- */

/** Next Friday's Jumu'ah from the timetable (or the Jumu'ah settings when the timetable has none). */
function nextFriday(env: Env, now: Date) {
  const n = nextJumuah(env.masjid, env.timetable, now);
  if (!n) return null;
  return { day: n.day, slots: n.slots, at: zonedToInstantSafe(n.day, n.slots[0]!.jamaah, env.masjid.timezone) };
}
const zonedToInstantSafe = (day: string, t: string, tz: string) => zonedToInstant(day, t, tz);
const slotLine = (s: { khutbah: string; jamaah: string }) => (s.khutbah ? `Khutbah ${s.khutbah} · Jama‘ah ${s.jamaah}` : `Jama‘ah ${s.jamaah}`);

function jumuah(c: HTMLElement): WidgetView {
  let p: JumuahProps;
  const wrap = el('div', 'nvd-center');
  const label = el('div', 'nvd-label'), big = el('div', 'big'), sub = el('div', 'nvd-sub');
  const slots = el('div', 'slots');
  wrap.append(label, big, sub, slots);
  c.append(wrap);
  let key = '', times = false;
  return {
    update(w) {
      p = w.props as JumuahProps;
      label.textContent = p.title || 'Jumu‘ah';
      key = '';
      times = designOf(w) === 'times';
      slots.style.display = times ? '' : 'none';
      big.style.display = times ? 'none' : '';
      sub.style.display = times ? 'none' : '';
    },
    tick({ now }, env) {
      const nf = nextFriday(env, now);
      if (!nf) { slots.replaceChildren(); return setText(big, '—') || setText(sub, env.mode === 'screen' ? '' : 'Add Jumu‘ah times to your timetable'); }
      let ch = false;
      if (key !== nf.day + nf.slots.map((x) => x.jamaah).join()) {
        key = nf.day + nf.slots.map((x) => x.jamaah).join();
        slots.replaceChildren(...nf.slots.map((s, i) => {
          const row = el('div', 'slot');
          // The jama'ah time is the big one; the khutbah sits beside it, smaller.
          const t = el('span', 'sl-t');
          if (s.khutbah) t.append(el('span', 'sl-k', `Khutbah ${s.khutbah}`));
          t.append(el('span', 'sl-j', s.jamaah));
          row.append(el('span', 'sl-n', nf.slots.length > 1 ? (i === 0 ? '1st Jumu‘ah' : '2nd Jumu‘ah') : 'Jumu‘ah'), t);
          return row;
        }));
        ch = setText(sub, nf.slots.length > 1 ? nf.slots.map((s, i) => `${i === 0 ? '1st' : '2nd'} ${s.jamaah}`).join(' · ') : slotLine(nf.slots[0]!)) || true;
      }
      if (times) return ch;
      const diff = nf.at.getTime() - now.getTime();
      return setText(big, diff <= 0 ? 'Now' : compactDuration(diff)) || ch;
    },
    destroy() {},
  };
}

/* ---------- Countdown ---------- */

function countdown(c: HTMLElement): WidgetView {
  let p: CountdownProps;
  const wrap = el('div', 'nvd-center');
  const label = el('div', 'nvd-label'), big = el('div', 'big nvd-digits'), sub = el('div', 'nvd-sub');
  wrap.append(label, big, sub);
  c.append(wrap);
  return {
    update(w) { p = w.props as CountdownProps; },
    tick({ now, state, day }, env) {
      const tz = env.masjid.timezone;
      let target: Date | null = null, what = '';
      if (p.target === 'custom') {
        const at = p.customAt ? new Date(p.customAt) : null;
        if (at && Number.isFinite(at.getTime())) { target = at; what = p.customLabel || 'Event'; }
      } else if (!state.empty) {
        const m = p.target === 'nextJamaah' ? state.nextJamaah ?? state.nextStart : state.nextStart;
        target = m.at;
        const jum = m.prayer === 'dhuhr' && state.today.isFriday && m.day === day && state.today.jumuah.length > 0;
        what = `${jum ? 'Jumu‘ah' : PRAYER_LABEL[m.prayer]}${m.kind === 'jamaah' ? ' jama‘ah' : ''}`;
      }
      if (!target) return setText(label, p.label || 'Countdown') || setText(big, '—');
      const diff = target.getTime() - now.getTime();
      let ch = setText(label, p.target === 'custom' ? (p.label || what) : `${what} in`);
      ch = setText(big, diff > 0 ? clockDuration(diff) : '00:00') || ch;
      ch = setText(sub, `at ${hhmm(target, tz)}`) || ch;
      return ch;
    },
    destroy() {},
  };
}

/* ---------- rotating cards (Announcements) ---------- */

function rotator(c: HTMLElement, label: HTMLElement) {
  const box = el('div', 'rot'), dots = el('div', 'dots');
  c.append(label, box, dots);
  let items: HTMLElement[] = [], i = 0, seconds = 10, last = 0;
  return {
    set(nodes: HTMLElement[], sec: number) {
      items = nodes; seconds = Math.max(3, sec || 10); i = 0; last = 0;
      box.replaceChildren(...nodes);
      dots.replaceChildren(...nodes.map(() => el('i')));
      dots.style.display = nodes.length > 1 ? '' : 'none';
      this.show(0);
    },
    show(k: number) {
      items.forEach((n, j) => n.classList.toggle('on', j === k));
      [...dots.children].forEach((d, j) => d.classList.toggle('on', j === k));
    },
    tick(now: Date) {
      if (items.length < 2) return;
      const t = Math.floor(now.getTime() / 1000);
      if (!last) last = t;
      if (t - last >= seconds) { last = t; i = (i + 1) % items.length; this.show(i); }
    },
  };
}

function announcements(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label ann-h');
  const rot = rotator(c, label);
  return {
    update(w, env) {
      const p = w.props as AnnouncementsProps;
      label.textContent = p.title || 'Announcements';
      const list = (p.items ?? []).filter((a) => a.title || a.body);
      const nodes = list.map((a) => {
        const it = el('div', 'it ans-i');
        if (a.title) it.append(el('div', 'tx ans-t', a.title));
        if (a.body) it.append(el('div', 'nvd-sub ans-b', a.body));
        return it;
      });
      if (!nodes.length && env.mode !== 'screen') { const it = el('div', 'it'); it.append(el('div', 'nvd-sub nvd-faint', 'Add announcements in the inspector')); nodes.push(it); }
      rot.set(nodes, p.seconds);
    },
    tick({ now }) { rot.tick(now); return false; },
    destroy() {},
  };
}

/* ---------- Posters ---------- */

function posters(c: HTMLElement): WidgetView {
  const ps = el('div', 'ps');
  c.append(ps);
  let imgs: HTMLImageElement[] = [], i = 0, last = 0, seconds = 12;
  return {
    update(w, env) {
      const p = w.props as PostersProps;
      seconds = Math.max(3, p.seconds || 12);
      ps.className = `ps fit-${p.fit === 'contain' ? 'contain' : 'cover'}${p.transition === 'slide' ? ' slide' : ''}`;
      let items = (p.items ?? []).filter((x) => x.src);
      // The design picker shows sample posters when none are uploaded, so every design can be told apart.
      if (!items.length && env.mode === 'thumbnail') items = D2.SAMPLE_POSTERS.map((src, k) => ({ id: `s${k}`, src, alt: '' }));
      imgs = items.map((x, k) => {
        const img = el('img', k === 0 ? 'on' : '');
        img.alt = x.alt ?? '';
        img.decoding = 'async';
        img.loading = k < 2 ? 'eager' : 'lazy';
        img.src = x.src;
        return img;
      });
      i = 0; last = 0;
      ps.replaceChildren(...imgs);
      if (!imgs.length && env.mode !== 'screen') {
        const e = el('div', 'ps-empty');
        e.append(el('div', '', 'Posters'), el('div', 'nvd-sub', 'Add images in the inspector'));
        ps.append(e);
      }
    },
    tick({ now }) {
      if (imgs.length < 2) return false;
      const t = Math.floor(now.getTime() / 1000);
      if (!last) last = t;
      if (t - last >= seconds) {
        last = t; const prev = i; i = (i + 1) % imgs.length;
        imgs[i]!.classList.add('on'); imgs[prev]!.classList.remove('on');
      }
      return false;
    },
    destroy() { imgs.forEach((im) => { im.src = ''; }); },
  };
}

/* ---------- Donations ---------- */

/* ---------- Donation QR ---------- */

export function qrSvg(text: string): string {
  const qr = qrcode(0, 'M');
  qr.addData(text || ' ');
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r++) for (let col = 0; col < n; col++) if (qr.isDark(r, col)) d += `M${col} ${r}h1v1h-1z`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-1 -1 ${n + 2} ${n + 2}" shape-rendering="crispEdges" role="img" aria-label="QR code"><path fill="#000" d="${d}"/></svg>`;
}

function donationQr(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label'), box = el('div', 'qr'), cap = el('div', 'nvd-sub');
  c.append(label, box, cap);
  let lastUrl = '';
  return {
    update(w) {
      const p = w.props as DonationQrProps;
      label.textContent = p.title || 'Donate';
      cap.textContent = p.caption || '';
      cap.style.display = p.caption ? '' : 'none';
      const url = /^https?:\/\//i.test(p.url) ? p.url : '';
      if (url !== lastUrl) {
        lastUrl = url;
        // The SVG is generated locally from a validated URL; it contains only one path element.
        box.innerHTML = url ? qrSvg(url) : '';
      }
    },
    tick() { return false; },
    destroy() {},
  };
}

/* ---------- Ayah / Hadith ---------- */

function scripture(c: HTMLElement, kind: 'ayah' | 'hadith'): WidgetView {
  const wrap = el('div', 'nvd-center');
  const label = el('div', 'nvd-label'), ar = el('div', 'tx-ar'), tx = el('div', 'tx'), ref = el('div', 'nvd-sub nvd-faint');
  wrap.append(label, ar, tx, ref);
  c.append(wrap);
  let p: ScriptureProps, key = '';
  const render = (day: string) => {
    const pick = scriptureFor(kind, p, day);
    const ch = setText(tx, pick.text ? `“${pick.text}”` : '');
    setText(ref, refLine(pick, p));
    const showAr = !!pick.arabic && p.showArabic !== false;
    ar.style.display = showAr ? '' : 'none';
    if (showAr) setText(ar, pick.arabic!);
    return ch;
  };
  return {
    update(w, env) { p = w.props as ScriptureProps; label.textContent = p.title || (kind === 'ayah' ? 'Ayah of the Day' : 'Hadith'); key = ''; void env; },
    tick({ day }) { if (day === key) return false; key = day; return render(day) || true; },
    destroy() {},
  };
}

/** "Abu Huraira · Sahih al-Bukhari 6114" (the narrator can be hidden). */
export const refLine = (s: { reference: string; narrator?: string }, p: ScriptureProps) => [p.showNarrator !== false ? s.narrator : '', s.reference].filter(Boolean).join(' · ');

/* ---------- Heading / Paragraph (custom text) ---------- */

/**
 * Text people type themselves. Never more than the limit (older or imported designs are cut there too), and fitted
 * like every other widget: it fills its box at the set text size and shrinks just enough to stay inside it.
 */
function customText(c: HTMLElement, kind: 'heading' | 'paragraph'): WidgetView {
  const wrap = el('div', 'nvd-center ctx');
  const title = el('div', 'nvd-label ctx-t'), tx = el('div', `tx ctx-${kind === 'heading' ? 'h' : 'p'}`), rule = el('div', 'ctx-rule');
  wrap.append(title, tx, rule);
  c.append(wrap);
  return {
    update(w, env) {
      const p = w.props as HeadingProps & Partial<ParagraphProps>;
      const d = designOf(w);
      wrap.className = `nvd-center ctx ctx-${kind} ctx-${d}`;
      // A banner's text is white or near-black, whichever reads better on its colour.
      const ink = rgbToHex(autoInk(hexToRgb(w.accent)));
      wrap.style.color = d === 'banner' && !w.textColor ? ink : '';
      // Title strip: the paragraph's title sits on a strip of the accent colour.
      title.style.color = d === 'header' ? ink : '';
      const text = String(p.text ?? '').slice(0, kind === 'heading' ? HEADING_MAX : PARAGRAPH_MAX).trim();
      const placeholder = env.mode === 'editor' && !text;
      tx.textContent = text || (placeholder ? (kind === 'heading' ? 'Your heading' : 'Type your text in the panel') : '');
      tx.classList.toggle('nvd-faint', placeholder);
      const t = kind === 'paragraph' ? String(p.title ?? '').slice(0, PARAGRAPH_TITLE_MAX).trim() : '';
      title.textContent = t;
      title.style.display = t ? '' : 'none';
    },
    tick() { return false; },
    destroy() {},
  };
}

/* ---------- Events ---------- */

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function events(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label'), list = el('div', 'ev');
  c.append(label, list);
  let p: EventsProps, key = '', mode: Env['mode'] = 'screen';
  const render = (day: string) => {
    const items = (p.items ?? []).filter((e) => e.title && e.date >= day).sort((a, b) => (a.date + (a.time ?? '')).localeCompare(b.date + (b.time ?? ''))).slice(0, Math.max(1, p.max || 4));
    list.replaceChildren(...items.map((e) => {
      const { month, day: d } = parseYmd(e.date);
      const row = el('div', 'ev-i'), badge = el('div', 'ev-d'), text = el('div', 'nvd-grow');
      badge.append(el('b', '', String(d)), el('small', '', MON[month - 1] ?? ''));
      text.append(el('div', 'ev-t', e.title), el('div', 'ev-m', [e.time, e.place].filter(Boolean).join(' · ') || (e.date === day ? 'Today' : '')));
      row.append(badge, text);
      return row;
    }));
    if (!items.length) list.append(el('div', 'nvd-sub nvd-faint', mode === 'screen' ? '' : 'Add events in the inspector'));
  };
  return {
    update(w, env) { p = w.props as EventsProps; mode = env.mode; label.textContent = p.title || 'Upcoming'; key = ''; },
    tick({ day }) { if (day === key) return false; key = day; render(day); return true; },
    destroy() {},
  };
}

/* ---------- News Ticker ---------- */

function ticker(c: HTMLElement): WidgetView {
  const tk = el('div', 'tk'), tag = el('div', 'tk-tag'), win = el('div', 'tk-win'), run = el('div', 'tk-run');
  win.append(run);
  tk.append(tag, win);
  c.append(tk);
  c.style.padding = '0';
  let speed = 60, sig = '';
  const measure = () => {
    // One copy scrolls past, then a second identical copy takes its place: a seamless, GPU-only loop.
    const first = run.firstElementChild as HTMLElement | null;
    if (!first) return;
    const w = [...run.children].slice(0, run.children.length / 2).reduce((s, n) => s + (n as HTMLElement).offsetWidth, 0);
    if (!w) return;
    run.style.setProperty('--tk-d', `${-w}px`);
    run.style.setProperty('--tk-t', `${(w / Math.max(10, speed * 2.2)).toFixed(2)}s`);
    run.classList.add('go');
  };
  return {
    update(w) {
      const p = w.props as TickerProps;
      speed = p.speed || 60;
      tag.textContent = p.tag || '';
      tag.style.display = p.tag ? '' : 'none';
      const texts = (p.items ?? []).map((i) => i.text).filter(Boolean);
      const s = texts.join('\u0000') + '|' + speed;
      if (s === sig) return;
      sig = s;
      run.classList.remove('go');
      const make = () => texts.map((t) => el('span', '', t));
      run.replaceChildren(...make(), ...make());
      requestAnimationFrame(measure);
    },
    tick() { return false; },
    destroy() { run.classList.remove('go'); },
  };
}

/* ---------- Makruh Times ---------- */

/** "Makruh time · please do not pray any salah until 07:23": what the banner scrolls, for a period. */
export function makruhText(m: MakruhPeriod | null, tz: string): string {
  if (!m) return 'Makruh time · Please do not pray any salah';
  const until = hhmm(m.until, tz);
  return m.kind === 'sunrise' ? `Makruh time after sunrise · Please do not pray any salah until ${until}` : `Zawal · Makruh time · Please do not pray any salah until Dhuhr begins at ${until}`;
}

/**
 * The makruh banner: solid red, white text scrolling continuously. On screens it shows only during a makruh time (the
 * Stage hides it otherwise and lays it over the news ticker); in the Studio it shows the next period's message.
 */
function makruh(c: HTMLElement): WidgetView {
  const t = ticker(c);
  let base: Widget | null = null, p: MakruhProps, msg = '';
  // The text follows the banner's height (about 44% of it), so it reads as well on a portrait screen as on landscape.
  const sizeToBanner = () => { const h = c.clientHeight; if (h) c.style.setProperty('--mk-h', `${h}px`); };
  const show = (text: string, env: Env) => {
    sizeToBanner();
    if (!base || text === msg) return false;
    msg = text;
    t.update({ ...base, props: { tag: 'MAKRUH', speed: 55, items: [{ id: 'a', text }, { id: 'b', text }] } } as Widget, env);
    return true;
  };
  const textAt = (now: Date, env: Env) => {
    const cur = makruhAt(env.masjid, env.timetable, now, p);
    return makruhText(cur ?? (env.mode === 'editor' ? nextMakruh(env.masjid, env.timetable, now, p) : null), env.masjid.timezone);
  };
  return {
    update(w, env) { base = w; p = w.props as MakruhProps; msg = ''; show(textAt(new Date(), env), env); },
    tick(k, env) { return show(textAt(k.now, env), env) || t.tick(k, env); },
    destroy() { t.destroy(); },
  };
}

/* ---------- Masjid logo ---------- */

function logo(c: HTMLElement): WidgetView {
  const box = el('div', 'nvd-logo');
  c.append(box);
  let src = '';
  return {
    update(w, env) {
      const next = env.masjid.logo ?? '';
      box.className = `nvd-logo fit-${(w.props as LogoProps).fit === 'cover' ? 'cover' : 'contain'}`;
      if (next === src && box.firstChild) return;
      src = next;
      if (next) {
        const img = el('img');
        img.alt = env.masjid.name;
        img.decoding = 'async';
        img.src = next;
        box.replaceChildren(img);
      } else if (env.mode !== 'screen') {
        const e = el('div', 'ps-empty');
        e.append(el('div', '', 'Masjid logo'), el('div', 'nvd-sub', 'Add your logo in Settings'));
        box.replaceChildren(e);
      } else box.replaceChildren();
    },
    tick() { return false; },
    destroy() { box.replaceChildren(); },
  };
}

type Factory = (content: HTMLElement) => WidgetView;

/**
 * A widget whose design can change (e.g. digital → analog clock). When the design changes, the old view is removed
 * and the new one built in its place; otherwise updates go straight to the current view.
 */
function designed(designs: Record<string, Factory>): Factory {
  return (c) => {
    let cur: WidgetView | null = null, name = '';
    const ensure = (w: Widget) => {
      const d = designOf(w);
      const next = designs[d] ? d : Object.keys(designs)[0]!;
      if (cur && next === name) return cur;
      cur?.destroy();
      c.replaceChildren();
      c.removeAttribute('style');
      c.className = `nvd-c d-${next}`;
      name = next;
      cur = designs[next]!(c);
      return cur;
    };
    return {
      update(w, env) { ensure(w).update(w, env); },
      tick(t, env) { return cur ? cur.tick(t, env) : false; },
      destroy() { cur?.destroy(); cur = null; },
    };
  };
}

const ramadan = (k: 'iftar' | 'sehri' | 'tarawih') => designed({
  time: (c) => D.ramadanWidget(c, k),
  countdown: (c) => timerFace(c, 'digital', ramadanSource(k)),
  ...Object.fromEntries((['ring', 'bar', 'flip', 'arc'] as const).map((f) => [f, (c: HTMLElement) => timerFace(c, f, ramadanSource(k))])),
});

/** The Digital LED design: the same layout as `f`, its face marked so the numbers draw in seven-segment digits. */
const led = (f: Factory): Factory => (c) => {
  const v = f(c);
  const mark = () => { for (const k of c.children) k.classList.add('led-face'); };
  mark();
  return { ...v, update: (w, env) => { v.update(w, env); mark(); }, tick: (t, env) => { const ch = v.tick(t, env); mark(); return ch; } };
};

export const FACTORIES: Record<Widget['type'], Factory> = {
  prayerTimes: designed({ tiles: prayerTimes, icons: D.prayerIcons, clocks: D.prayerClocks, table: D.prayerTable, focus: D.prayerFocus, timeline: D2.prayerTimeline, board: D.prayerBoard, led: led(D.prayerBoard) }),
  clock: designed({
    digital: clock, analog: (c) => D.analogClock(c, 'classic'), 'analog-minimal': (c) => D.analogClock(c, 'minimal'),
    'analog-arabic': (c) => D.analogClock(c, 'arabic'), flip: D.flipClock, stacked: D.stackedClock, led: led(clock),
  }),
  jumuah: designed({ countdown: jumuah, times: jumuah, focus: D.jumuahFocus, ring: (c) => timerFace(c, 'ring', jumuahSource()), flip: (c) => timerFace(c, 'flip', jumuahSource()), bar: (c) => timerFace(c, 'bar', jumuahSource()) }),
  countdown: designed({ digital: countdown, led: led(countdown), ...Object.fromEntries((['ring', 'hourglass', 'flip', 'bar', 'words'] as const).map((k) => [k, (c: HTMLElement) => timerFace(c, k, countdownSource())])) }),
  announcements: designed({ slides: announcements, list: D.announcementsList, grid: D.announcementsGrid, featured: D2.announcementsFeatured, headlines: D2.announcementsHeadlines, alert: D.alertDesign }),
  posters: designed({ single: posters, ...Object.fromEntries((['framed', 'zoom', 'progress', 'stack', 'counter'] as const).map((k) => [k, (c: HTMLElement) => D2.postersMore(c, k)])), flyin: D2.postersFly }),
  donations: designed({ bar: DN.donationBar, segments: DN.donationSegments, cards: DN.donationCards, ring: DN.donationRing, numbers: DN.donationNumbers, gauge: DN.donationGauge }),
  donationQr: designed({ card: donationQr, side: (c) => D.qrDesign(c, 'side', qrSvg), minimal: (c) => D.qrDesign(c, 'minimal', qrSvg), scanner: (c) => D2.qrMore(c, 'scanner', qrSvg), poster: (c) => D2.qrMore(c, 'poster', qrSvg), accent: (c) => D2.qrMore(c, 'accent', qrSvg) }),
  ayah: designed({ centered: (c) => scripture(c, 'ayah'), quote: (c) => D.scriptureDesign(c, 'ayah', 'quote'), minimal: (c) => D.scriptureDesign(c, 'ayah', 'minimal'), card: (c) => D2.scriptureMore(c, 'ayah', 'card'), split: (c) => D2.scriptureMore(c, 'ayah', 'split'), highlight: (c) => D2.scriptureMore(c, 'ayah', 'highlight') }),
  hadith: designed({ centered: (c) => scripture(c, 'hadith'), quote: (c) => D.scriptureDesign(c, 'hadith', 'quote'), minimal: (c) => D.scriptureDesign(c, 'hadith', 'minimal'), card: (c) => D2.scriptureMore(c, 'hadith', 'card'), split: (c) => D2.scriptureMore(c, 'hadith', 'split'), highlight: (c) => D2.scriptureMore(c, 'hadith', 'highlight') }),
  events: designed({ list: events, cards: (c) => D.eventsDesign(c, 'cards'), timeline: (c) => D.eventsDesign(c, 'timeline'), agenda: D2.eventsAgenda, spotlight: D2.eventsSpotlight, days: D2.eventsDays }),
  ticker: designed({ tag: ticker, plain: (c) => D.tickerDesign(c, 'plain'), pills: (c) => D.tickerDesign(c, 'pills'), fade: D2.tickerFade, headline: (c) => D2.tickerScroll(c, 'headline'), clock: (c) => D2.tickerScroll(c, 'clock') }),
  logo: designed({ plain: logo, ...Object.fromEntries((['name', 'stacked', 'badge', 'plate', 'wordmark'] as const).map((k) => [k, (c: HTMLElement) => D2.logoMore(c, k)])) }),
  iftar: ramadan('iftar'),
  sehri: ramadan('sehri'),
  tarawih: ramadan('tarawih'),
  heading: designed(Object.fromEntries((['plain', 'underline', 'bar', 'banner', 'outline', 'colour'] as const).map((k) => [k, (c: HTMLElement) => customText(c, 'heading')]))),
  makruh,
  paragraph: designed(Object.fromEntries((['plain', 'accent', 'card', 'quote', 'outline', 'header'] as const).map((k) => [k, (c: HTMLElement) => customText(c, 'paragraph')]))),
};

/** Times for a day, cached so the once-a-second tick never recalculates the sun's position. */
export function makeDayCache() {
  const cache = new Map<string, ReturnType<typeof dayTimes>>();
  return (m: Masjid, tt: TimetableIndex, day: string) => {
    let v = cache.get(day);
    if (!v) { v = dayTimes(m, tt, day); cache.set(day, v); if (cache.size > 6) cache.delete(cache.keys().next().value!); }
    return v;
  };
}
